import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, X } from "lucide-react";
import { useInfiniteProducts } from "../features/catalog/api";
import ProductCard from "../features/catalog/ProductCard";
import SCSpinner from "../components/loaders/SCSpinner";
import ErrorState from "../components/ui/ErrorState";
import useDebouncedValue from "../hooks/useDebouncedValue";
import useInfiniteScrollTrigger from "../hooks/useInfiniteScrollTrigger";
import useSEO from "../hooks/useSEO";

export default function Search() {
  useSEO({ title: "Search" });
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const debouncedQuery = useDebouncedValue(query, 300);

  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteProducts({
    q: debouncedQuery,
  });
  const products = data?.pages.flatMap((page) => page.products) ?? [];
  const sentinelRef = useInfiniteScrollTrigger(() => fetchNextPage(), { enabled: !!hasNextPage });

  function handleChange(value) {
    setQuery(value);
    setParams(value ? { q: value } : {}, { replace: true });
  }

  return (
    <div className="px-4 py-6">
      <div className="relative">
        <SearchIcon size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-light" />
        <input
          autoFocus
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Search products…"
          className="admin-input pl-10 pr-9"
        />
        {query && (
          <button
            type="button"
            onClick={() => handleChange("")}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-light"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {!debouncedQuery ? (
        <p className="mt-10 text-center text-sm text-ink-light">Start typing to search the catalog.</p>
      ) : isLoading ? (
        <div className="flex justify-center py-16">
          <SCSpinner size={56} />
        </div>
      ) : isError ? (
        <ErrorState message="Couldn't search right now." onRetry={refetch} />
      ) : products.length === 0 ? (
        <p className="mt-10 text-center text-sm text-ink-light">No products match “{debouncedQuery}”.</p>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 lg:grid-cols-4">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
          {hasNextPage && (
            <div ref={sentinelRef} className="flex justify-center py-8">
              {isFetchingNextPage && <SCSpinner size={32} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
